import { Injectable } from '@angular/core';
import { GlobalService } from './global.service';
import { JwtService } from './jwt.service';

type RestaurantOrderType = 'Dine-in' | 'Takeaway' | 'inRoom';

export interface RestaurantOrderContext {
  restaurantId: string;
  inclusiveMode: boolean;
  orderType: RestaurantOrderType;
  delivery: 'Restaurant' | 'Room';
  tableNumber: number | null;
  roomNumber: string;
  roomOrderToken: string;
  appendToOrderId?: string;
}

@Injectable({ providedIn: 'root' })
export class RestaurantCartStorageService {
  private readonly activeCartKey = 'activeRestaurantCartStorageKey';
  private readonly anonymousSessionKey = 'restaurantAnonymousCartSessionId';

  constructor(
    private globalService: GlobalService,
    private jwtService: JwtService
  ) {}

  load(restaurantId: string, isRestaurantOrder = false): any[] {
    const key = this.getStorageKey(restaurantId, isRestaurantOrder);
    this.setActiveKey(key);
    return this.readItems(key);
  }

  save(restaurantId: string, items: any[], isRestaurantOrder = false): void {
    const key = this.getStorageKey(restaurantId, isRestaurantOrder);
    localStorage.setItem(key, JSON.stringify(items || []));
    this.setActiveKey(key);
  }

  clear(restaurantId: string, isRestaurantOrder = false): void {
    const key = this.getStorageKey(restaurantId, isRestaurantOrder);
    localStorage.removeItem(key);
    if (sessionStorage.getItem(this.activeCartKey) === key) {
      sessionStorage.removeItem(this.activeCartKey);
    }
  }

  has(restaurantId: string, isRestaurantOrder = false): boolean {
    return !!localStorage.getItem(this.getStorageKey(restaurantId, isRestaurantOrder));
  }

  loadOrderContext(
    restaurantId: string,
    isRestaurantOrder = false
  ): RestaurantOrderContext | null {
    const key = this.getOrderContextStorageKey(restaurantId, isRestaurantOrder);
    try {
      const context = JSON.parse(localStorage.getItem(key) || 'null');
      return context && typeof context === 'object'
        ? (context as RestaurantOrderContext)
        : null;
    } catch {
      return null;
    }
  }

  saveOrderContext(
    restaurantId: string,
    context: RestaurantOrderContext,
    isRestaurantOrder = false
  ): void {
    const key = this.getOrderContextStorageKey(restaurantId, isRestaurantOrder);
    localStorage.setItem(key, JSON.stringify(context || {}));
  }

  clearOrderContext(restaurantId: string, isRestaurantOrder = false): void {
    localStorage.removeItem(
      this.getOrderContextStorageKey(restaurantId, isRestaurantOrder)
    );
  }

  loadActive(): any[] {
    const key = sessionStorage.getItem(this.activeCartKey);
    if (!key || !key.startsWith('restaurant-cart:v2:')) {
      return [];
    }
    return this.readItems(key);
  }

  clearActive(): void {
    const key = sessionStorage.getItem(this.activeCartKey);
    if (key && key.startsWith('restaurant-cart:v2:')) {
      localStorage.removeItem(key);
      localStorage.removeItem(
        key.replace(
          'restaurant-cart:v2:',
          'restaurant-order-context:v2:'
        )
      );
    }
    sessionStorage.removeItem(this.activeCartKey);
  }

  getActiveStorageKey(): string | null {
    const key = sessionStorage.getItem(this.activeCartKey);
    return key && key.startsWith('restaurant-cart:v2:') ? key : null;
  }

  migrateActiveCartToCurrentOwner(restaurantId: string, isRestaurantOrder = false): void {
    const sourceKey = this.getActiveStorageKey();
    if (!sourceKey || !restaurantId) {
      return;
    }

    const targetKey = this.getStorageKey(restaurantId, isRestaurantOrder);
    if (sourceKey === targetKey) {
      return;
    }

    localStorage.setItem(targetKey, JSON.stringify(this.readItems(sourceKey)));
    localStorage.removeItem(sourceKey);
    this.setActiveKey(targetKey);
  }

  private getStorageKey(restaurantId: string, isRestaurantOrder: boolean): string {
    return `restaurant-cart:v2:${restaurantId}:${this.getCartOwner(isRestaurantOrder)}`;
  }

  private getOrderContextStorageKey(
    restaurantId: string,
    isRestaurantOrder: boolean
  ): string {
    return `restaurant-order-context:v2:${restaurantId}:${this.getCartOwner(
      isRestaurantOrder
    )}`;
  }

  private getCartOwner(isRestaurantOrder: boolean): string {
    if (isRestaurantOrder) {
      try {
        const portalUser: any = this.jwtService.decodeJwttoken(this.jwtService.getToken());
        const portalUserId = portalUser?._id || portalUser?.id || portalUser?.UserId;
        if (portalUser?.HotelId && portalUserId) {
          return `restaurant-order:${portalUser.HotelId}:staff:${portalUserId}`;
        }
      } catch (error) {}
    }

    const checkIn = this.decodeToken('CheckIn_TOKEN');
    if (checkIn?.HotelId && checkIn?.UserId) {
      return `checkin:${checkIn.HotelId}:${checkIn.UserId}`;
    }

    const login = this.decodeToken('LogIn_TOKEN');
    if (login?.HotelId && login?.id) {
      return `login:${login.HotelId}:${login.id}`;
    }

    return `anonymous:${this.globalService.getHostname()}:${this.getAnonymousSessionId()}`;
  }

  private setActiveKey(key: string): void {
    sessionStorage.setItem(this.activeCartKey, key);
  }

  private readItems(key: string): any[] {
    try {
      const items = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  private decodeToken(key: string): any {
    try {
      const token = localStorage.getItem(key) || sessionStorage.getItem(key);
      return token ? this.jwtService.decodeJwttoken(token) : null;
    } catch {
      return null;
    }
  }

  private getAnonymousSessionId(): string {
    let id = sessionStorage.getItem(this.anonymousSessionKey);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(this.anonymousSessionKey, id);
    }
    return id;
  }
}
